import React from 'react';
import { CentralEdgeDevice } from '@simcheck/shared-types';
import { Cpu, HardDrive, Thermometer, Clock, Server, Code, MonitorSmartphone } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { useDateFormat } from '@/hooks/useDateFormat';

interface HealthTabProps {
  device: CentralEdgeDevice;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function getPercentColor(value: number): string {
  if (value > 80) return 'text-destructive';
  if (value > 60) return 'text-yellow-500';
  return 'text-emerald-500';
}

function getPercentBg(value: number): string {
  if (value > 80) return 'bg-destructive';
  if (value > 60) return 'bg-yellow-500';
  return 'bg-emerald-500';
}

function ProgressBar({ value, label, icon: Icon }: { value: number | null | undefined; label: string; icon: React.ElementType }) {
  if (value == null) {
    return (
      <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
        <Icon className="w-5 h-5 text-muted-foreground" />
        <div className="flex-1">
          <div className="text-sm font-medium text-foreground">{label}</div>
          <div className="text-xs text-muted-foreground">No data</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
      <Icon className={`w-5 h-5 ${getPercentColor(value)}`} />
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-foreground">{label}</span>
          <span className={`text-sm font-semibold ${getPercentColor(value)}`}>{value}%</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${getPercentBg(value)}`}
            style={{ width: `${Math.min(value, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export const HealthTab: React.FC<HealthTabProps> = ({ device }) => {
  const fmt = useDateFormat();
  const h = device.health;
  const version = device.softwareVersion || (device as any).version;

  return (
    <div className="space-y-6">
      {/* Version & Update Status */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Software Version:</span>
          <code className="text-sm font-semibold text-foreground">{version || 'Unknown'}</code>
        </div>
        {device.updateStatus && (
          <Badge variant={
            device.updateStatus === 'up_to_date' ? 'secondary' :
            device.updateStatus === 'update_available' ? 'outline' :
            device.updateStatus === 'updating' ? 'default' :
            'destructive'
          }>
            {device.updateStatus === 'up_to_date' ? 'Up to date' :
             device.updateStatus === 'update_available' ? 'Update available' :
             device.updateStatus === 'updating' ? 'Updating...' :
             'Update failed'}
          </Badge>
        )}
        {device.desiredVersion && device.desiredVersion !== version && (
          <span className="text-xs text-muted-foreground">
            Target: <code>{device.desiredVersion}</code>
          </span>
        )}
      </div>

      {/* Health Metrics */}
      {!h || h.cpuPercent == null ? (
        <div className="text-center py-12 text-muted-foreground">
          <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No health data available yet.</p>
          <p className="text-xs mt-1">Health reports are sent every ~2.5 minutes when the device is online.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProgressBar value={h.cpuPercent} label="CPU Usage" icon={Cpu} />
          <ProgressBar value={h.memoryPercent} label="Memory Usage" icon={HardDrive} />
          <ProgressBar value={h.diskPercent} label="Disk Usage" icon={HardDrive} />

          {/* Temperature */}
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <Thermometer className={`w-5 h-5 ${h.temperature != null && h.temperature > 70 ? 'text-destructive' : h.temperature != null && h.temperature > 55 ? 'text-yellow-500' : 'text-emerald-500'}`} />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">CPU Temperature</span>
                <span className={`text-sm font-semibold ${h.temperature != null && h.temperature > 70 ? 'text-destructive' : 'text-foreground'}`}>
                  {h.temperature != null ? `${h.temperature}°C` : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Uptime */}
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <Clock className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Uptime</span>
                <span className="text-sm font-semibold text-foreground">
                  {h.uptimeSeconds != null ? formatUptime(h.uptimeSeconds) : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Modem Count */}
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <MonitorSmartphone className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Connected Modems</span>
                <span className="text-sm font-semibold text-foreground">
                  {h.modemCount ?? 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Last Report Time */}
      {h?.lastReport && (
        <div className="text-xs text-muted-foreground text-right">
          Last health report: {(() => {
            const r = h.lastReport as any;
            if (r?._seconds) return fmt.formatDateTime(r._seconds * 1000);
            if (r?.seconds) return fmt.formatDateTime(r.seconds * 1000);
            const d = new Date(r);
            return isNaN(d.getTime()) ? 'N/A' : fmt.formatDateTime(d);
          })()}
        </div>
      )}
    </div>
  );
};
